import { AbstractService } from "cruzo";
import type { BookTitleSource } from "site/services/book-translations";
import { Lang, trs } from "site/translate";
import { ruleIdFromZeptoContent } from "site/services/zepto-editor";
import {
  finalizeLessonTimeline,
  getTimelineCompletedUnits,
  getTimelineTotalUnits,
  findTimelineIndexByRuleId,
  resolveRestoreTimelineIndex,
  type LessonTimelineDraftEntry,
  type LessonTimelineEntry,
} from "site/services/lesson-timeline";
import {
  collectLessonRuleEntries,
  normalizeLessonRule,
  ruleLessonItemStepCount,
  type LessonRuleEntry,
  type LessonRulePayload,
  type RuleLessonItem,
  type RuleZeptoRef,
} from "site/services/lesson-rule-slides";
import {
  collectLessonVocabularyEntries,
  type LessonWordEntry,
} from "site/services/lesson-vocabulary";
import {
  buildExerciseEditorRowHref,
  resolveStepItemZeptoRowId,
  zeptoRowNumberFromId,
} from "site/services/exercise-editor-nav";
import { parseLessonTranslationsPart } from "site/services/lesson-translations";
import { playerService } from "site/services/player.service";
import {
  clearLessonProgressFromStorage,
  readLessonProgressFromStorage,
  type SavedLessonProgress,
  writeLessonProgressToStorage,
} from "site/services/lesson-progress-storage";
import { deleteLessonProgressOnServer } from "site/services/user-sync.service";
import {
  exerciseStageButton,
  exerciseStageDescription,
  exerciseStageHasButton,
} from "site/services/exercise-stage-i18n";
import { i18nService } from "site/services/i18n";
import { scrollExerciseStageToTop } from "site/utils";

export interface Step { descriptor: StepDescriptor, data: any, subIndex: number, index: number }
export interface Stage { descriptor: StageDescriptor, index: number }

export interface StageDescriptor { key: string; description?: string; button?: string }

export interface StepDescriptor {
  key: 'dictionaries' | 'dialogs' | 'groups' | 'single' | 'select' | 'sentences' | 'rules',
  stages?: StageDescriptor[],
  order: number,
}

export const stepDescriptors: StepDescriptor[] = [
  { key: "groups", stages: [{ key: "choose" }], order: 5 },
  { key: "select", stages: [{ key: "choose" }], order: 3 },
  { key: "sentences", stages: [{ key: "read" }], order: 2.2 },
  { key: "rules", stages: [{ key: "read" }], order: 2.5 },
  {
    key: "dictionaries",
    stages: [{ key: "rememberWords" }, { key: "checkWords" }],
    order: 2,
  },
  {
    key: "dialogs",
    stages: [{ key: "chooseReply" }, { key: "chooseWord" }],
    order: 1,
  },
  { key: "single", stages: [{ key: "choose" }], order: 6 },
];

stepDescriptors.sort((a,b) => a.order - b.order)

export type Exercise = {
  id: number;
  title_arabic?: string;
  title_russian?: string;
  title_en?: string;
  title_fr?: string;
  type_icon_text?: string;
  type?: string;
  zepto: any[];
  audio: Record<string, string>;
  images: Record<string, string>;
};

enum TYPES {
  'new_word' = 1,
  'sentence' = 2,
  'task' = 3,
  'dialog' = 4,
  'build' = 5,
  'select' = 6,
  'rule' = 7,
}

const DIC_SIZE = 4
const DIC_MIN_SIZE = 2
const DIALOG_SIZE = 1000

class AppService extends AbstractService {
  data = null as any;

  arabicRules: Record<string, unknown> | null = null;

  trs$ = this.newRx(trs);
  currentLanguage$ = this.newRx<Lang>(Lang.ru);

  exercise$ = this.newRx<Exercise>(null);
  lessonBookTitle$ = this.newRx('');

  lessonBookTitleMeta$ = this.newRx<BookTitleSource | null>(null);

  lessonBookListHref$ = this.newRx('');

  lessonProgressBookId = "";
  lessonProgressExerciseId = "";
  curStage$ = this.newRx<Stage>(null);
  curStep$ = this.newRx<Step>(null);
  readonly uiLang$ = i18nService.lang$;

  stageDescriptionText(
    step: Step | null | undefined,
    stage: Stage | null | undefined,
    _localeTrigger?: unknown,
  ): string {
    void _localeTrigger;
    return exerciseStageDescription(step?.descriptor?.key, stage?.descriptor?.key, i18nService.lang$.actual);
  }

  stageButtonText(
    step: Step | null | undefined,
    stage: Stage | null | undefined,
    _localeTrigger?: unknown,
  ): string {
    void _localeTrigger;
    return exerciseStageButton(step?.descriptor?.key, stage?.descriptor?.key, i18nService.lang$.actual);
  }

  stageHasForwardButton(stage: Stage | null | undefined): boolean {
    const step = this.curStep$.actual;
    return exerciseStageHasButton(step?.descriptor?.key, stage?.descriptor?.key);
  }
  curStepItemIndex$ = this.newRx<number>(1);
  currentExerciseZeptoRowId$ = this.newRx<number | null>(null);
  lessonCompleted$ = this.newRx<boolean>(false);

  lessonTransitionActive = false;
  stageTransitionTick$ = this.newRx(0);
  private pendingStageChange: (() => void) | null = null;

  subscriptionAccessRevision$ = this.newRx(0);

  sessionPermissions$ = this.newRx<string[]>([]);

  subscriptionAccessLoading$ = this.newRx(false);

  private subscriptionAccessLoadingListeners = new Set<(loading: boolean) => void>();

  lessonProgressRevision$ = this.newRx(0);

  /** Index within `{step}` slides of the current rule exercise (0-based). */
  currentRuleStepIndex$ = this.newRx(0);
  /** Position in zepto-ordered lesson timeline (rules interleaved with exercises). */
  curTimelineIndex$ = this.newRx(-1);

  stageNavigationLocked$ = this.newRx(false);

  private stageNavigationLockUntil = 0;
  private stageNavigationUnlockTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly STAGE_NAV_LOCK_MS = 450;
  private static readonly STAGE_VEIL_LOCK_MS = 300;

  constructor() {
    super();
  }

  getLessonRulesUsed(): LessonRuleEntry[] {
    return collectLessonRuleEntries(this.exercise$.actual?.zepto, this.arabicRules);
  }

  getLessonWordsUsed(): LessonWordEntry[] {
    const exercise = this.exercise$.actual;
    return collectLessonVocabularyEntries(exercise?.zepto, exercise?.audio, exercise?.images);
  }

  getLessonRule(ruleId: string | null | undefined): LessonRulePayload | null {
    if (!ruleId || !this.arabicRules) {
      return null;
    }
    return normalizeLessonRule(this.arabicRules[ruleId]);
  }

  setSubscriptionAccessLoading(loading: boolean): void {
    const next = !!loading;
    if (this.subscriptionAccessLoading$.actual === next) {
      return;
    }
    this.subscriptionAccessLoading$.update(next);
    for (const fn of this.subscriptionAccessLoadingListeners) {
      fn(next);
    }
  }

  notifySubscriptionAccessChanged(): void {
    const cur = this.subscriptionAccessRevision$.actual;
    const n = typeof cur === "number" && Number.isFinite(cur) ? cur : 0;
    this.subscriptionAccessRevision$.update(n + 1);
  }

  notifyLessonProgressChanged(): void {
    const cur = this.lessonProgressRevision$.actual;
    const n = typeof cur === "number" && Number.isFinite(cur) ? cur : 0;
    this.lessonProgressRevision$.update(n + 1);
  }

  setCurrentExerciseZeptoRowId(rowId: number | null) {
    const next = rowId != null && Number.isFinite(rowId) ? rowId : null;
    if (this.currentExerciseZeptoRowId$.actual === next) {
      return;
    }
    this.currentExerciseZeptoRowId$.update(next);
  }

  getCurrentRuleLessonItem(): RuleLessonItem | null {
    const step = this.curStep$.actual;
    if (step?.descriptor?.key !== "rules") {
      return null;
    }
    const item = step.data?.[step.subIndex];
    return item && typeof item === "object" ? (item as RuleLessonItem) : null;
  }

  canAdvanceRuleStep(): boolean {
    const item = this.getCurrentRuleLessonItem();
    if (!item) {
      return false;
    }
    return this.currentRuleStepIndex$.actual < ruleLessonItemStepCount(item) - 1;
  }

  setCurrentRuleStepIndex(index: number) {
    const item = this.getCurrentRuleLessonItem();
    const max = item ? ruleLessonItemStepCount(item) - 1 : 0;
    const next = Math.max(0, Math.min(max, index));
    if (this.currentRuleStepIndex$.actual === next) {
      return;
    }
    this.currentRuleStepIndex$.update(next);
    this.notifyLessonProgressChanged();
  }

  private stepDescriptorIndex(key: StepDescriptor["key"]): number {
    return stepDescriptors.findIndex((d) => d.key === key);
  }

  private getTimeline(): LessonTimelineEntry[] | null {
    const timeline = this.data?.timeline;
    return Array.isArray(timeline) && timeline.length ? timeline : null;
  }

  private usesTimelineNavigation(): boolean {
    return !!this.getTimeline()?.length;
  }

  private applyTimelineIndex(
    index: number,
    opts?: { stageIndex?: number; ruleStepIndex?: number },
  ): boolean {
    const timeline = this.getTimeline();
    if (!timeline || index < 0 || index >= timeline.length) {
      return false;
    }

    const entry = timeline[index]!;
    this.curTimelineIndex$.update(index);

    if (entry.kind === "rule") {
      const descIndex = this.stepDescriptorIndex("rules");
      const descriptor = stepDescriptors[descIndex]!;
      this.curStep$.update({
        descriptor,
        data: [entry.item],
        subIndex: 0,
        index: descIndex,
      });
      const stage = descriptor.stages![0]!;
      this.curStage$.update({ index: 0, descriptor: stage });
      this.currentRuleStepIndex$.update(opts?.ruleStepIndex ?? 0);
    } else {
      const descIndex = this.stepDescriptorIndex(entry.stepKey);
      const descriptor = stepDescriptors[descIndex]!;
      this.curStep$.update({
        descriptor,
        data: this.data[entry.stepKey],
        subIndex: entry.subIndex,
        index: descIndex,
      });
      const maxStage = Math.max(0, (descriptor.stages?.length ?? 1) - 1);
      const stageIndex = Math.max(0, Math.min(opts?.stageIndex ?? 0, maxStage));
      this.curStage$.update({ index: stageIndex, descriptor: descriptor.stages![stageIndex]! });
      this.currentRuleStepIndex$.update(0);
    }

    this.curStepItemIndex$.update(1);
    return true;
  }

  syncCurrentExerciseZeptoRowFromStep() {
    const id = resolveStepItemZeptoRowId(this.curStep$.actual, this.curStepItemIndex$.actual || 1);
    this.setCurrentExerciseZeptoRowId(id);
  }

  getExerciseEditorHrefForCurrentRow(): string | null {
    const rowId = this.currentExerciseZeptoRowId$.actual;
    const zepto = this.exercise$.actual?.zepto;
    const bookId = this.lessonProgressBookId;
    const exerciseId = this.lessonProgressExerciseId;
    if (rowId == null || !Array.isArray(zepto) || !bookId || !exerciseId) {
      return null;
    }
    const rowNumber = zeptoRowNumberFromId(zepto, rowId);
    if (rowNumber == null) {
      return null;
    }
    return buildExerciseEditorRowHref(bookId, exerciseId, rowNumber);
  }

  setLessonProgressContext(bookId: string, exerciseId: string) {
    this.lessonProgressBookId = bookId
    this.lessonProgressExerciseId = exerciseId
  }

  persistLessonProgressSnapshot(): void {
    const bookId = this.lessonProgressBookId;
    const exerciseId = this.lessonProgressExerciseId;
    if (!bookId || !exerciseId || !this.data) {
      return;
    }

    if (this.lessonCompleted$.actual) {
      writeLessonProgressToStorage(bookId, exerciseId, {
        stepKey: "",
        stageKey: "",
        subIndex: 0,
        stageIndex: 0,
        curStepItemIndex: 0,
        lessonCompleted: true,
        progressPercent: 100,
      });
      this.notifyLessonProgressChanged();
      return;
    }

    const curStep = this.curStep$.actual;
    const curStage = this.curStage$.actual;
    if (!curStep?.descriptor || !curStage?.descriptor) {
      return;
    }

    let timelineIndex: number | undefined;
    let stepItemId: number | undefined;
    if (this.usesTimelineNavigation()) {
      timelineIndex = this.curTimelineIndex$.actual;
      if (timelineIndex < 0 && curStep.descriptor.key === "rules") {
        const item = curStep.data?.[curStep.subIndex] as RuleLessonItem | undefined;
        const ruleId = Number(item?.id);
        if (Number.isFinite(ruleId) && ruleId > 0) {
          stepItemId = ruleId;
          timelineIndex = findTimelineIndexByRuleId(this.data.timeline, ruleId);
        }
      }
      if (timelineIndex < 0) {
        timelineIndex = undefined;
      }
    }
    if (curStep.descriptor.key === "rules") {
      const item = curStep.data?.[curStep.subIndex] as RuleLessonItem | undefined;
      const ruleId = Number(item?.id);
      if (Number.isFinite(ruleId) && ruleId > 0) {
        stepItemId = ruleId;
      }
    }

    writeLessonProgressToStorage(bookId, exerciseId, {
      stepKey: curStep.descriptor.key,
      stageKey: curStage.descriptor.key,
      subIndex: curStep.subIndex,
      stageIndex: curStage.index,
      curStepItemIndex: this.curStepItemIndex$.actual,
      lessonCompleted: false,
      progressPercent: this.getLessonProgressPercent(),
      timelineIndex,
      ruleStepIndex: this.currentRuleStepIndex$.actual,
      stepItemId,
    });

    this.notifyLessonProgressChanged();
  }

  restoreLessonProgress(savedOverride?: SavedLessonProgress | null): boolean {
    const bookId = this.lessonProgressBookId;
    const exerciseId = this.lessonProgressExerciseId;
    if (!bookId || !exerciseId || !this.data) {
      return false;
    }

    const saved = savedOverride ?? readLessonProgressFromStorage(bookId, exerciseId);
    if (!saved) {
      return false;
    }

    if (saved.lessonCompleted) {
      this.lessonCompleted$.update(true);
      this.curStep$.update(null);
      this.curStage$.update(null);
      this.curStepItemIndex$.update(0);
      return true;
    }

    if (this.usesTimelineNavigation()) {
      const ruleStepIndex = Math.max(0, Math.floor(saved.ruleStepIndex ?? 0));
      const timelineIndex = resolveRestoreTimelineIndex(this.data.timeline, saved);

      if (timelineIndex < 0) {
        return false;
      }

      this.lessonCompleted$.update(false);
      if (
        !this.applyTimelineIndex(timelineIndex, {
          stageIndex: saved.stageIndex,
          ruleStepIndex,
        })
      ) {
        return false;
      }

      const cii = Math.max(0, Math.floor(saved.curStepItemIndex));
      this.curStepItemIndex$.update(cii > 0 ? cii : 1);
      return true;
    }

    const step = this.rebuildStepFromSaved(this.data, saved.stepKey, saved.subIndex);
    if (!step) {
      return false;
    }

    const stage = this.rebuildStageFromSaved(step.descriptor, saved.stageKey, saved.stageIndex);
    if (!stage?.descriptor) {
      return false;
    }

    this.lessonCompleted$.update(false);
    this.curStep$.update(step);
    this.curStage$.update(stage);
    const cii = Math.max(0, Math.floor(saved.curStepItemIndex));
    this.curStepItemIndex$.update(cii > 0 ? cii : 1);

    return true;
  }

  private rebuildStepFromSaved(data: any, stepKey: string, subIndex: number): Step | null {
    if (!data || typeof stepKey !== "string" || !stepKey) {
      return null;
    }
    const stepIndex = stepDescriptors.findIndex((d) => d.key === stepKey);
    if (stepIndex < 0) {
      return null;
    }
    const descriptor = stepDescriptors[stepIndex];
    const arr = data[descriptor.key];
    if (!Array.isArray(arr)) {
      return null;
    }
    const si = Math.floor(subIndex);
    if (si < 0 || si >= arr.length || !this.hasStepItem(arr[si])) {
      return null;
    }
    return {
      data: arr,
      descriptor,
      index: stepIndex,
      subIndex: si,
    };
  }

  private rebuildStageFromSaved(descriptor: StepDescriptor, stageKey: string, stageIndex: number): Stage | null {
    const stages = descriptor.stages;
    if (!stages?.length) {
      return null;
    }
    let idx = typeof stageKey === "string" && stageKey ? stages.findIndex((s) => s.key === stageKey) : -1;
    if (idx < 0) {
      idx = Math.min(Math.max(0, Math.floor(stageIndex)), stages.length - 1);
    } else {
      idx = Math.min(idx, stages.length - 1);
    }
    const st = stages[idx];
    if (!st) {
      return null;
    }
    return { index: idx, descriptor: st };
  }

  setLessonTransitionActive(active: boolean) {
    this.lessonTransitionActive = active;
    if (!active) {
      this.pendingStageChange = null;
    }
  }

  requestStageChange(apply: () => void) {
    if (!this.lessonTransitionActive) {
      apply();
      return;
    }

    this.pendingStageChange = apply;
    this.stageTransitionTick$.update(this.stageTransitionTick$.actual + 1);
  }

  consumePendingStageChange() {
    const apply = this.pendingStageChange;
    this.pendingStageChange = null;
    apply?.();
  }

  hasPendingStageChange(): boolean {
    return this.pendingStageChange != null;
  }

  isStageNavigationLocked(): boolean {
    return Date.now() < this.stageNavigationLockUntil;
  }

  lockStageNavigation(ms = AppService.STAGE_NAV_LOCK_MS) {
    this.stageNavigationLockUntil = Math.max(
      this.stageNavigationLockUntil,
      Date.now() + ms,
    );
    this.stageNavigationLocked$.update(true);
    if (this.stageNavigationUnlockTimer) {
      clearTimeout(this.stageNavigationUnlockTimer);
    }
    const delay = Math.max(0, this.stageNavigationLockUntil - Date.now());
    this.stageNavigationUnlockTimer = setTimeout(() => {
      this.stageNavigationUnlockTimer = null;
      if (this.isStageNavigationLocked()) {
        this.lockStageNavigation(0);
        return;
      }
      this.stageNavigationLocked$.update(false);
    }, delay);
  }

  releaseStageNavigationLock() {
    this.stageNavigationLockUntil = 0;
    if (this.stageNavigationUnlockTimer) {
      clearTimeout(this.stageNavigationUnlockTimer);
      this.stageNavigationUnlockTimer = null;
    }
    this.stageNavigationLocked$.update(false);
  }

  resetLessonProgressOnLogout(): void {
    this.lessonProgressBookId = "";
    this.lessonProgressExerciseId = "";
    this.notifyLessonProgressChanged();
  }

  completeCurrentStage(): void {
    if (!this.data || this.lessonCompleted$.actual) {
      return;
    }

    const step = this.curStep$.actual;
    const stage = this.curStage$.actual;
    if (!step?.descriptor || !stage?.descriptor) {
      return;
    }

    const stages = step.descriptor.stages;
    if (stages && stage.index < stages.length - 1) {
      this.updateStage();
      return;
    }

    this.updateStep();
  }

  clearExerciseSession() {
    this.exercise$.update(null);
    this.data = null;
    this.arabicRules = null;
    this.curStep$.update(null);
    this.curStage$.update(null);
    this.curStepItemIndex$.update(1);
    this.currentExerciseZeptoRowId$.update(null);
    this.currentRuleStepIndex$.update(0);
    this.releaseStageNavigationLock();
    this.curTimelineIndex$.update(-1);
    this.lessonCompleted$.update(false);
    this.lessonBookTitle$.update('');
    this.lessonBookTitleMeta$.update(null);
    this.lessonBookListHref$.update('');
    this.lessonProgressBookId = "";
    this.lessonProgressExerciseId = "";
  }

  getData(zepto: any[]) {
    const normalizeText = (value: any) => {
      if (typeof value === "string") {
        return value.includes("\n") ? value.split("\n") : [value];
      }
      if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
        return value;
      }
      return [""];
    };

    const dictionaries: any[] = [];
    let curDictionariesCursor = -1;

    const dialogs: any[] = [];
    let curDialogCursor = -1;

    const groups: any[] = [];
    let curTaskCursor = 0;

    const select = [];

    const sentences = [];

    const single = [];

    const rulesRaw: RuleZeptoRef[] = [];

    const timelineDraft: LessonTimelineDraftEntry[] = [];
    const pendingRules: RuleZeptoRef[] = [];

    const flushPendingRules = () => {
      for (const ref of pendingRules) {
        timelineDraft.push({ kind: "rule", ref });
      }
      pendingRules.length = 0;
    };

    const commitExerciseTimeline = (stepKey: StepDescriptor["key"], subIndex: number) => {
      flushPendingRules();
      timelineDraft.push({ kind: "exercise", stepKey, subIndex });
    };

    let prevType = null

    for (let index = 0; index < zepto.length; index++) {
      const element = zepto[index];
      const type = element[0];
      const content = element[1];
      const id = element[2];
      const nextType = zepto[index + 1]?.[0];
      const text = normalizeText(content?.[0]);
      const translations = parseLessonTranslationsPart(content?.[1]);
      switch (type) {
        case TYPES.new_word: {
          const startsNewChunk =
            prevType !== TYPES.new_word || (dictionaries[curDictionariesCursor]?.length ?? 0) === DIC_SIZE;
          if (startsNewChunk) {
            curDictionariesCursor++;
            dictionaries[curDictionariesCursor] = [];
          } else {
            dictionaries[curDictionariesCursor] = dictionaries[curDictionariesCursor] || [];
          }
          dictionaries[curDictionariesCursor].push({ text, translations, id, type });
          const chunkFull = dictionaries[curDictionariesCursor].length >= DIC_SIZE;
          const chunkEnds = chunkFull || nextType !== TYPES.new_word || index === zepto.length - 1;
          if (chunkEnds) {
            commitExerciseTimeline("dictionaries", curDictionariesCursor);
          }
          break;
        }
        case TYPES.dialog: {
          const startsNewDialog =
            prevType !== TYPES.dialog || (dialogs[curDialogCursor]?.length ?? 0) === DIALOG_SIZE;
          if (startsNewDialog) {
            curDialogCursor++;
            dialogs[curDialogCursor] = [];
          } else {
            dialogs[curDialogCursor] = dialogs[curDialogCursor] || [];
          }
          dialogs[curDialogCursor].push({ text, translations, id, type });
          const dialogFull = dialogs[curDialogCursor].length >= DIALOG_SIZE;
          const dialogEnds = dialogFull || nextType !== TYPES.dialog || index === zepto.length - 1;
          if (dialogEnds) {
            commitExerciseTimeline("dialogs", curDialogCursor);
          }
          break;
        }
        case TYPES.sentence:
          sentences.push({ text, translations, id, type });
          commitExerciseTimeline("sentences", sentences.length - 1);
          break;
        case TYPES.build:
          single.push({ text, translations, id, type });
          commitExerciseTimeline("single", single.length - 1);
          break;
        case TYPES.select: {
          const arabicText = text
            .map((part) => String(part ?? "").trim())
            .filter(Boolean)
            .join("\n");
          select.push({
            text: arabicText ? [arabicText] : text,
            translations,
            id,
            type,
          });
          commitExerciseTimeline("select", select.length - 1);
          break;
        }
        case TYPES.task:
          groups[curTaskCursor] = groups[curTaskCursor] || [];
          groups[curTaskCursor].push({ text, translations, id, type });
          const groupFull = groups[curTaskCursor].length >= 5;
          const groupEnds = groupFull || nextType !== TYPES.task || index === zepto.length - 1;
          if (groupEnds) {
            commitExerciseTimeline("groups", curTaskCursor);
          }
          if (groupFull) curTaskCursor++;
          break;
        case TYPES.rule: {
          const ref = { id, ruleId: ruleIdFromZeptoContent(content) };
          pendingRules.push(ref);
          rulesRaw.push(ref);
          break;
        }
      }

      prevType = type
    }

    let dicIndex = 0;
    while (dicIndex < dictionaries.length - 1) {
      const currentChunk = dictionaries[dicIndex] || [];
      const nextChunk = dictionaries[dicIndex + 1] || [];

      while (currentChunk.length < DIC_SIZE && nextChunk.length > 0) {
        currentChunk.push(nextChunk.shift());
      }

      dictionaries[dicIndex] = currentChunk;

      if (nextChunk.length === 0) {
        dictionaries.splice(dicIndex + 1, 1);
        continue;
      }

      dictionaries[dicIndex + 1] = nextChunk;
      dicIndex++;
    }

    while (dictionaries.length > 1) {
      const lastChunk = dictionaries[dictionaries.length - 1];
      if (lastChunk.length >= DIC_MIN_SIZE) {
        break;
      }

      const prevChunk = dictionaries[dictionaries.length - 2];
      if (prevChunk.length <= DIC_MIN_SIZE) {
        break;
      }

      lastChunk.unshift(prevChunk.pop());
    }

    flushPendingRules();

    return { dialogs, single, dictionaries, groups, select, sentences, rulesRaw, timelineDraft }
  }

  finalizeLessonData(data: any) {
    if (!data) {
      return data;
    }
    data.timeline = finalizeLessonTimeline(data.timelineDraft ?? [], this.arabicRules);
    delete data.timelineDraft;
    data.rules = [];
    delete data.rulesRaw;
    return data;
  }

  findNextStep(data: any, curStepIndex: number, curSubIndex: number): Step | null {
    const hasStepContent = (stepData: any) => {
      if (!Array.isArray(stepData) || stepData.length === 0) {
        return false;
      }

      return stepData.some((item) => {
        if (Array.isArray(item)) {
          return item.length > 0;
        }

        return !!item;
      });
    };

    const hasStepItem = (item: any) => {
      if (Array.isArray(item)) {
        return item.length > 0;
      }
      return !!item;
    };

    const startStepIndex = Math.max(0, curStepIndex);

    for (let stepIndex = startStepIndex; stepIndex < stepDescriptors.length; stepIndex++) {
      const descriptor = stepDescriptors[stepIndex];
      const curStepData = data?.[descriptor.key];

      if (!hasStepContent(curStepData)) {
        continue;
      }

      const startSubIndex = stepIndex === curStepIndex ? curSubIndex + 1 : 0;
      for (let subIndex = startSubIndex; subIndex < curStepData.length; subIndex++) {
        if (!hasStepItem(curStepData[subIndex])) {
          continue;
        }

        return {
          data: curStepData,
          descriptor,
          index: stepIndex,
          subIndex,
        };
      }
    }

    return null;
  }

  findNextStage(curDescriptor: StepDescriptor, curStageIndex: number): Stage {
    const descriptor = curDescriptor.stages[++curStageIndex]

    return { index: curStageIndex, descriptor }
  }

  findPrevStep(data: any, curStepIndex: number, curSubIndex: number): Step | null {
    if (!data) {
      return null;
    }

    const startStepIndex = Math.min(Math.max(0, curStepIndex), stepDescriptors.length - 1);

    for (let stepIndex = startStepIndex; stepIndex >= 0; stepIndex--) {
      const descriptor = stepDescriptors[stepIndex];
      const curStepData = data?.[descriptor.key];
      if (!Array.isArray(curStepData) || curStepData.length === 0) {
        continue;
      }

      const startSubIndex = stepIndex === curStepIndex
        ? Math.min(curSubIndex - 1, curStepData.length - 1)
        : curStepData.length - 1;

      for (let subIndex = startSubIndex; subIndex >= 0; subIndex--) {
        if (!this.hasStepItem(curStepData[subIndex])) {
          continue;
        }

        return {
          data: curStepData,
          descriptor,
          index: stepIndex,
          subIndex,
        };
      }
    }

    return null;
  }

  private hasStepItem(item: any) {
    if (Array.isArray(item)) {
      return item.length > 0;
    }
    return !!item;
  }

  private getDescriptorItemsCount(descriptor: StepDescriptor) {
    const items = this.data?.[descriptor.key];
    if (!Array.isArray(items)) {
      return 0;
    }

    if (descriptor.key === "rules") {
      let total = 0;
      for (let i = 0; i < items.length; i += 1) {
        if (this.hasStepItem(items[i])) {
          total += ruleLessonItemStepCount(items[i] as RuleLessonItem);
        }
      }
      return total;
    }

    let total = 0;
    for (let i = 0; i < items.length; i++) {
      if (this.hasStepItem(items[i])) {
        total++;
      }
    }

    return total;
  }

  getTotalLessonItemsCount() {
    if (!this.data) {
      return 0;
    }

    const timeline = this.getTimeline();
    if (timeline) {
      return getTimelineTotalUnits(timeline, stepDescriptors);
    }

    let total = 0;
    for (let i = 0; i < stepDescriptors.length; i++) {
      total += this.getDescriptorItemsCount(stepDescriptors[i]);
    }

    return total;
  }

  getCompletedLessonItemsCount() {
    if (!this.data) {
      return 0;
    }

    if (this.lessonCompleted$.actual) {
      return this.getTotalLessonItemsCount();
    }

    const timeline = this.getTimeline();
    if (timeline) {
      return getTimelineCompletedUnits(
        timeline,
        stepDescriptors,
        this.curTimelineIndex$.actual,
        this.curStage$.actual?.index ?? 0,
        this.currentRuleStepIndex$.actual,
      );
    }

    const curStep = this.curStep$.actual;
    if (!curStep) {
      return 0;
    }

    let completed = 0;
    for (let i = 0; i < stepDescriptors.length; i++) {
      const descriptor = stepDescriptors[i];
      const items = this.data?.[descriptor.key];
      if (!Array.isArray(items)) {
        continue;
      }

      if (i < curStep.index) {
        completed += this.getDescriptorItemsCount(descriptor);
        continue;
      }

      if (i > curStep.index) {
        break;
      }

      const subIndex = Math.max(0, Number(curStep.subIndex || 0));
      for (let j = 0; j < items.length; j += 1) {
        if (!this.hasStepItem(items[j])) {
          continue;
        }
        if (j < subIndex) {
          if (descriptor.key === "rules") {
            completed += ruleLessonItemStepCount(items[j] as RuleLessonItem);
          } else {
            completed += 1;
          }
          continue;
        }
        if (j === subIndex) {
          if (descriptor.key === "rules") {
            completed += this.currentRuleStepIndex$.actual;
          }
          break;
        }
      }
    }

    return completed;
  }

  getLessonProgressPercent() {
    const total = this.getTotalLessonItemsCount();
    if (!total) {
      return 0;
    }

    const completed = this.getCompletedLessonItemsCount();
    return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
  }

  canGoBack() {
    if (!this.data) {
      return false;
    }

    if (this.lessonCompleted$.actual) {
      return this.getTotalLessonItemsCount() > 0;
    }

    const curStep = this.curStep$.actual;
    const curStage = this.curStage$.actual;
    if (!curStep || !curStage) {
      return false;
    }

    if (this.usesTimelineNavigation()) {
      if (curStep.descriptor?.key === "rules" && this.currentRuleStepIndex$.actual > 0) {
        return true;
      }
      if (curStage.index > 0) {
        return true;
      }
      return this.curTimelineIndex$.actual > 0;
    }

    if (curStep.descriptor?.key === "rules" && this.currentRuleStepIndex$.actual > 0) {
      return true;
    }

    return curStage.index > 0 || !!this.findPrevStep(this.data, curStep.index, curStep.subIndex);
  }

  restartLesson(): void {
    if (!this.data) {
      return;
    }

    const bookId = this.lessonProgressBookId;
    const exerciseId = this.lessonProgressExerciseId;

    void playerService.stopCurrentPlayer();

    this.lessonCompleted$.update(false);
    this.curStep$.update(null);
    this.curStage$.update(null);
    this.curStepItemIndex$.update(1);
    this.curTimelineIndex$.update(-1);

    if (bookId && exerciseId) {
      clearLessonProgressFromStorage(bookId, exerciseId);
      void deleteLessonProgressOnServer(bookId, exerciseId);
      this.notifyLessonProgressChanged();
    }

    this.updateStep();
    scrollExerciseStageToTop("auto");
  }

  updateStep() {
    const curStep = this.curStep$.actual;
    const change = () => {
      if (this.usesTimelineNavigation()) {
        const curIdx = this.curTimelineIndex$.actual;
        const nextIdx = curIdx < 0 ? 0 : curIdx + 1;
        if (!this.applyTimelineIndex(nextIdx)) {
          appService.curStep$.update(null);
          appService.curStepItemIndex$.update(0);
          appService.curStage$.update(null);
          appService.curTimelineIndex$.update(this.getTimeline()?.length ?? 0);
          appService.lessonCompleted$.update(true);
          this.persistLessonProgressSnapshot();
          return;
        }

        appService.lessonCompleted$.update(false);
        this.persistLessonProgressSnapshot();
        return;
      }

      const step = appService.findNextStep(
        this.data,
        curStep ? curStep.index : -1,
        curStep ? curStep.subIndex : -1,
      );

      if (!step) {
        appService.curStep$.update(null);
        appService.curStepItemIndex$.update(0);
        appService.curStage$.update(null);
        appService.lessonCompleted$.update(true);
        this.persistLessonProgressSnapshot();
        return;
      }

      appService.lessonCompleted$.update(false);
      appService.curStep$.update(step);
      appService.curStepItemIndex$.update(1);
      appService.currentRuleStepIndex$.update(0);

      const stages = step.descriptor.stages;
      if (stages?.length) {
        appService.curStage$.update({ index: 0, descriptor: stages[0] });
      } else {
        appService.curStage$.update(null);
      }

      this.persistLessonProgressSnapshot();
    };

    if (!curStep) {
      change();
      return;
    }

    this.requestStageChange(change);
  }

  updateStage() {
    const curStep = appService.curStep$.actual;
    if (!curStep?.descriptor || this.isStageNavigationLocked()) {
      return;
    }

    if (curStep.descriptor.key === "rules") {
      if (this.canAdvanceRuleStep()) {
        this.lockStageNavigation();
        this.currentRuleStepIndex$.update(this.currentRuleStepIndex$.actual + 1);
        this.persistLessonProgressSnapshot();
        this.notifyLessonProgressChanged();
        return;
      }

      this.lockStageNavigation(AppService.STAGE_VEIL_LOCK_MS);
    } else {
      this.lockStageNavigation(AppService.STAGE_VEIL_LOCK_MS);
    }

    const stage = appService.findNextStage(
      curStep.descriptor,
      appService.curStage$.actual ? appService.curStage$.actual.index : -1,
    );

    if (!stage.descriptor) {
      this.updateStep();
      return;
    }

    this.requestStageChange(() => {
      appService.curStage$.update(stage);
      this.persistLessonProgressSnapshot();
    });
  }

  updatePrevStage() {
    if (!this.data || this.isStageNavigationLocked()) {
      return;
    }

    const curStage = this.curStage$.actual;
    const curStep = this.curStep$.actual;

    if (curStep?.descriptor?.key === "rules" && this.currentRuleStepIndex$.actual > 0) {
      this.lockStageNavigation();
      this.currentRuleStepIndex$.update(this.currentRuleStepIndex$.actual - 1);
      this.persistLessonProgressSnapshot();
      this.notifyLessonProgressChanged();
      return;
    }

    this.lockStageNavigation(AppService.STAGE_VEIL_LOCK_MS);

    if (this.lessonCompleted$.actual || !this.curStep$.actual) {
      if (this.usesTimelineNavigation()) {
        const timeline = this.getTimeline();
        if (!timeline?.length) {
          return;
        }
        this.requestStageChange(() => {
          appService.lessonCompleted$.update(false);
          const lastIndex = timeline.length - 1;
          const lastEntry = timeline[lastIndex]!;
          if (lastEntry.kind === "rule") {
            this.applyTimelineIndex(lastIndex, {
              ruleStepIndex: Math.max(0, ruleLessonItemStepCount(lastEntry.item) - 1),
            });
          } else {
            const descriptor = stepDescriptors.find((d) => d.key === lastEntry.stepKey);
            const lastStage = Math.max(0, (descriptor?.stages?.length ?? 1) - 1);
            this.applyTimelineIndex(lastIndex, { stageIndex: lastStage });
          }
          this.persistLessonProgressSnapshot();
        });
        return;
      }

      const prevStep = this.findPrevStep(this.data, stepDescriptors.length - 1, Number.MAX_SAFE_INTEGER);
      if (!prevStep) {
        return;
      }

      this.requestStageChange(() => {
        appService.lessonCompleted$.update(false);
        appService.curStep$.update(prevStep);
        appService.curStepItemIndex$.update(1);

        if (prevStep.descriptor?.key === "rules") {
          const item = prevStep.data?.[prevStep.subIndex] as RuleLessonItem | undefined;
          appService.currentRuleStepIndex$.update(Math.max(0, ruleLessonItemStepCount(item) - 1));
        } else {
          appService.currentRuleStepIndex$.update(0);
        }

        const stages = prevStep.descriptor.stages || [];
        const prevStageIndex = Math.max(0, stages.length - 1);
        appService.curStage$.update({ index: prevStageIndex, descriptor: stages[prevStageIndex] });
        this.persistLessonProgressSnapshot();
      });
      return;
    }

    if (curStage && curStage.index > 0 && curStep) {
      this.requestStageChange(() => {
        const prevStageIndex = curStage.index - 1;
        appService.curStage$.update({
          index: prevStageIndex,
          descriptor: curStep.descriptor.stages?.[prevStageIndex],
        });
        this.persistLessonProgressSnapshot();
      });
      return;
    }

    if (this.usesTimelineNavigation()) {
      const prevIdx = this.curTimelineIndex$.actual - 1;
      if (prevIdx < 0) {
        return;
      }
      const timeline = this.getTimeline()!;
      const prevEntry = timeline[prevIdx]!;
      this.requestStageChange(() => {
        if (prevEntry.kind === "rule") {
          this.applyTimelineIndex(prevIdx, {
            ruleStepIndex: Math.max(0, ruleLessonItemStepCount(prevEntry.item) - 1),
          });
        } else {
          const descriptor = stepDescriptors.find((d) => d.key === prevEntry.stepKey);
          const lastStage = Math.max(0, (descriptor?.stages?.length ?? 1) - 1);
          this.applyTimelineIndex(prevIdx, { stageIndex: lastStage });
        }
        this.persistLessonProgressSnapshot();
      });
      return;
    }

    const prevStep = this.findPrevStep(this.data, curStep.index, curStep.subIndex);
    if (!prevStep) {
      return;
    }

    this.requestStageChange(() => {
      appService.curStep$.update(prevStep);
      appService.curStepItemIndex$.update(1);

      if (prevStep.descriptor?.key === "rules") {
        const item = prevStep.data?.[prevStep.subIndex] as RuleLessonItem | undefined;
        appService.currentRuleStepIndex$.update(Math.max(0, ruleLessonItemStepCount(item) - 1));
      } else {
        appService.currentRuleStepIndex$.update(0);
      }

      const stages = prevStep.descriptor.stages || [];
      const prevStageIndex = Math.max(0, stages.length - 1);
      appService.curStage$.update({ index: prevStageIndex, descriptor: stages[prevStageIndex] });
      this.persistLessonProgressSnapshot();
    });
  }

  runAfterCorrect(_path: string | null | undefined, done: () => void) {
    setTimeout(done, 400);
  }
}

export const appService = new AppService();
